import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AlertModal from '../components/AlertModal';
import CompetenceMonthField from '../components/CompetenceMonthField';
import PageContainer from '../components/ui/PageContainer';
import api from '../services/api';
import { useClientPage } from '../components/useClientPage';

interface Payee {
  id: string;
  name: string;
  pixDocument: string | null;
  sortOrder: number;
}

interface SalaryLine {
  id: string;
  payeeId: string;
  salaryCents: number;
  paid: boolean;
  sourceInvoiceId?: string | null;
  sourceInvoiceLabel?: string | null;
}

interface Reimbursement {
  id: string;
  payeeId: string;
  amountCents: number;
  note: string | null;
}

interface FixedExpense {
  id: string;
  code: string;
  label: string;
  amountCents: number;
  pixKey: string | null;
  paid: boolean;
}

interface CleaningLog {
  id: string;
  occurredOn: string;
  amountCents: number;
}

interface PaymentsSheet {
  competence: string;
  payees: Payee[];
  salaries: SalaryLine[];
  reimbursements: Reimbursement[];
  fixedExpenses: FixedExpense[];
  cleanings: CleaningLog[];
}

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function currentCompetence(): string {
  const brazil = new Date(Date.now() - 3 * 60 * 60 * 1000);
  return brazil.toISOString().slice(0, 7);
}

function shiftCompetence(competence: string, delta: number): string {
  const [year, month] = competence.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function parseReaisToCents(raw: string): number | null {
  const text = raw.trim();
  if (!text) return 0;
  let normalized = text.replace(/\s/g, '');
  if (normalized.includes(',')) normalized = normalized.replace(/\./g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

const sectionTitleSx = { fontWeight: 800, mb: 1.5 } as const;

const PaymentsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [competence, setCompetence] = useState(currentCompetence);
  const [sheet, setSheet] = useState<PaymentsSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState('');
  const [newPayee, setNewPayee] = useState('');
  const [reimbPayeeId, setReimbPayeeId] = useState('');
  const [reimbAmount, setReimbAmount] = useState('');
  const [reimbNote, setReimbNote] = useState('');
  const [cleaningDate, setCleaningDate] = useState('');
  const [cleaningAmount, setCleaningAmount] = useState('');

  const load = useCallback(async (month: string): Promise<void> => {
    setLoading(true);
    try {
      const res = await api.get<PaymentsSheet>('/finance/payments', { params: { competence: month } });
      setSheet(res.data);
      setReimbPayeeId((current) => current || res.data.payees[0]?.id || '');
      setCleaningDate((current) => (current.startsWith(month) ? current : `${month}-01`));
    } catch {
      setAlert('Não foi possível carregar os pagamentos deste mês.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(competence);
  }, [competence, load]);

  const salaryByPayee = useMemo(() => {
    const map = new Map<string, SalaryLine>();
    sheet?.salaries.forEach((row) => map.set(row.payeeId, row));
    return map;
  }, [sheet]);

  const reimbursementByPayee = useMemo(() => {
    const map = new Map<string, Reimbursement[]>();
    sheet?.reimbursements.forEach((row) => {
      const list = map.get(row.payeeId) ?? [];
      list.push(row);
      map.set(row.payeeId, list);
    });
    return map;
  }, [sheet]);

  const payrollRows = useMemo(() => {
    if (!sheet) return [];
    return sheet.payees.map((payee) => {
      const salary = salaryByPayee.get(payee.id);
      const reimbursementCents = (reimbursementByPayee.get(payee.id) ?? []).reduce(
        (sum, row) => sum + row.amountCents,
        0,
      );
      return {
        payee,
        salary,
        reimbursementCents,
        totalCents: (salary?.salaryCents ?? 0) + reimbursementCents,
      };
    });
  }, [sheet, salaryByPayee, reimbursementByPayee]);

  const payrollTotal = payrollRows.reduce((sum, row) => sum + row.totalCents, 0);
  const fixedTotal = (sheet?.fixedExpenses ?? []).reduce((sum, row) => sum + row.amountCents, 0);
  const cleaningTotal = (sheet?.cleanings ?? []).reduce((sum, row) => sum + row.amountCents, 0);
  const salaryPage = useClientPage(payrollRows);
  const fixedPage = useClientPage(sheet?.fixedExpenses ?? []);
  const cleaningPage = useClientPage(sheet?.cleanings ?? []);
  const reimbPage = useClientPage(sheet?.reimbursements ?? []);

  const saveSalary = async (line: SalaryLine, raw: string): Promise<void> => {
    const cents = parseReaisToCents(raw);
    if (cents == null) {
      setAlert('Informe o salário em reais, por exemplo 3500,00.');
      return;
    }
    if (cents === line.salaryCents) return;
    try {
      await api.patch(`/finance/payments/salaries/${line.id}`, { salaryCents: cents });
      setSheet((current) =>
        current
          ? {
              ...current,
              salaries: current.salaries.map((row) => (row.id === line.id ? { ...row, salaryCents: cents } : row)),
            }
          : current,
      );
    } catch {
      setAlert('Não foi possível salvar o salário.');
    }
  };

  const toggleSalaryPaid = async (line: SalaryLine): Promise<void> => {
    try {
      await api.patch(`/finance/payments/salaries/${line.id}`, { paid: !line.paid });
      setSheet((current) =>
        current
          ? {
              ...current,
              salaries: current.salaries.map((row) => (row.id === line.id ? { ...row, paid: !line.paid } : row)),
            }
          : current,
      );
    } catch {
      setAlert('Não foi possível atualizar o status do pagamento.');
    }
  };

  const savePix = async (payee: Payee, raw: string): Promise<void> => {
    const pixDocument = raw.trim();
    if (pixDocument === (payee.pixDocument ?? '')) return;
    try {
      await api.patch(`/finance/payments/payees/${payee.id}`, { pixDocument });
      setSheet((current) =>
        current
          ? {
              ...current,
              payees: current.payees.map((row) => (row.id === payee.id ? { ...row, pixDocument: pixDocument || null } : row)),
            }
          : current,
      );
    } catch {
      setAlert('Não foi possível salvar o PIX.');
    }
  };

  const addPayee = async (): Promise<void> => {
    const name = newPayee.trim();
    if (!name) return;
    try {
      await api.post('/finance/payments/payees', { name });
      setNewPayee('');
      await load(competence);
    } catch {
      setAlert('Não foi possível incluir o prestador.');
    }
  };

  const addReimbursement = async (): Promise<void> => {
    const amountCents = parseReaisToCents(reimbAmount);
    if (!reimbPayeeId || amountCents == null) {
      setAlert('Escolha a pessoa e informe o valor do ressarcimento.');
      return;
    }
    try {
      await api.post('/finance/payments/reimbursements', {
        competence,
        payeeId: reimbPayeeId,
        amountCents,
        note: reimbNote.trim() || undefined,
      });
      setReimbAmount('');
      setReimbNote('');
      await load(competence);
    } catch {
      setAlert('Não foi possível lançar o ressarcimento.');
    }
  };

  const removeReimbursement = async (id: string): Promise<void> => {
    try {
      await api.delete(`/finance/payments/reimbursements/${id}`);
      await load(competence);
    } catch {
      setAlert('Não foi possível remover o ressarcimento.');
    }
  };

  const saveFixedAmount = async (row: FixedExpense, raw: string): Promise<void> => {
    const cents = parseReaisToCents(raw);
    if (cents == null) {
      setAlert('Informe o valor em reais, por exemplo 1200,00.');
      return;
    }
    if (cents === row.amountCents) return;
    try {
      await api.patch(`/finance/payments/fixed/${row.id}`, { amountCents: cents });
      setSheet((current) =>
        current
          ? {
              ...current,
              fixedExpenses: current.fixedExpenses.map((item) =>
                item.id === row.id ? { ...item, amountCents: cents } : item,
              ),
            }
          : current,
      );
    } catch {
      setAlert('Não foi possível salvar a despesa.');
    }
  };

  const saveFixedPix = async (row: FixedExpense, raw: string): Promise<void> => {
    const pixKey = raw.trim();
    if (pixKey === (row.pixKey ?? '')) return;
    try {
      await api.patch(`/finance/payments/fixed/${row.id}`, { pixKey });
      setSheet((current) =>
        current
          ? {
              ...current,
              fixedExpenses: current.fixedExpenses.map((item) =>
                item.id === row.id ? { ...item, pixKey: pixKey || null } : item,
              ),
            }
          : current,
      );
    } catch {
      setAlert('Não foi possível salvar a chave PIX.');
    }
  };

  const toggleFixedPaid = async (row: FixedExpense): Promise<void> => {
    try {
      await api.patch(`/finance/payments/fixed/${row.id}`, { paid: !row.paid });
      setSheet((current) =>
        current
          ? {
              ...current,
              fixedExpenses: current.fixedExpenses.map((item) =>
                item.id === row.id ? { ...item, paid: !row.paid } : item,
              ),
            }
          : current,
      );
    } catch {
      setAlert('Não foi possível atualizar o status da despesa.');
    }
  };

  const addCleaning = async (): Promise<void> => {
    const amountCents = parseReaisToCents(cleaningAmount);
    if (!cleaningDate || amountCents == null) {
      setAlert('Informe a data e o valor da limpeza.');
      return;
    }
    try {
      await api.post('/finance/payments/cleanings', { competence, occurredOn: cleaningDate, amountCents });
      setCleaningAmount('');
      await load(competence);
    } catch {
      setAlert('A data da limpeza precisa estar dentro do mês selecionado.');
    }
  };

  const removeCleaning = async (id: string): Promise<void> => {
    try {
      await api.delete(`/finance/payments/cleanings/${id}`);
      await load(competence);
    } catch {
      setAlert('Não foi possível remover o lançamento.');
    }
  };

  const content = (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <IconButton aria-label="Mês anterior" onClick={() => setCompetence((value) => shiftCompetence(value, -1))}>
          <ChevronLeftIcon />
        </IconButton>
        <CompetenceMonthField
          label="Competência"
          size="small"
          value={competence}
          onChange={setCompetence}
          minWidth={220}
        />
        <IconButton aria-label="Próximo mês" onClick={() => setCompetence((value) => shiftCompetence(value, 1))}>
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      {loading || !sheet ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Typography variant="h6" component="h2" sx={sectionTitleSx}>
              Pagamento de salário
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Prestador</TableCell>
                    <TableCell>Salário</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell>Ressarcimento</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Chave PIX</TableCell>
                    <TableCell>Situação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salaryPage.slice.map((row) => (
                    <TableRow key={row.payee.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{row.payee.name}</TableCell>
                      <TableCell>
                        {row.salary ? (
                          <TextField
                            size="small"
                            defaultValue={centsToInput(row.salary.salaryCents)}
                            key={`${row.salary.id}-${row.salary.salaryCents}`}
                            onBlur={(event) => void saveSalary(row.salary!, event.target.value)}
                            slotProps={{ htmlInput: { 'aria-label': `Salário de ${row.payee.name}`, inputMode: 'decimal' } }}
                            sx={{ width: 120 }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {row.salary?.sourceInvoiceLabel ? (
                          <Typography variant="body2" color="text.secondary">
                            {row.salary.sourceInvoiceLabel}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{brl.format(row.reimbursementCents / 100)}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{brl.format(row.totalCents / 100)}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          defaultValue={row.payee.pixDocument ?? ''}
                          key={`${row.payee.id}-${row.payee.pixDocument ?? ''}`}
                          placeholder="CPF, CNPJ, e-mail, telefone ou copia e cola"
                          onBlur={(event) => void savePix(row.payee, event.target.value)}
                          slotProps={{ htmlInput: { 'aria-label': `PIX de ${row.payee.name}`, maxLength: 1024 } }}
                          sx={{ width: 260 }}
                        />
                      </TableCell>
                      <TableCell>
                        {row.salary ? (
                          <Button
                            size="small"
                            variant={row.salary.paid ? 'contained' : 'outlined'}
                            color={row.salary.paid ? 'success' : 'inherit'}
                            onClick={() => void toggleSalaryPaid(row.salary!)}
                          >
                            {row.salary.paid ? 'Pago' : 'Não pago'}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontWeight: 800 }}>{brl.format(payrollTotal / 100)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {salaryPage.pager}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2, alignItems: { sm: 'flex-end' } }}>
              <TextField
                size="small"
                label="Novo prestador"
                value={newPayee}
                onChange={(event) => setNewPayee(event.target.value)}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => void addPayee()} sx={{ height: 40, minWidth: 120, flexShrink: 0 }}>
                Incluir
              </Button>
            </Stack>

            <Typography variant="h6" component="h2" sx={{ ...sectionTitleSx, mt: 4 }}>
              Contas fixas
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Conta</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Chave PIX</TableCell>
                    <TableCell>Situação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fixedPage.slice.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          defaultValue={centsToInput(row.amountCents)}
                          key={`${row.id}-amount-${row.amountCents}`}
                          onBlur={(event) => void saveFixedAmount(row, event.target.value)}
                          slotProps={{ htmlInput: { 'aria-label': `Valor de ${row.label}`, inputMode: 'decimal' } }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          defaultValue={row.pixKey ?? ''}
                          key={`${row.id}-pix-${row.pixKey ?? ''}`}
                          onBlur={(event) => void saveFixedPix(row, event.target.value)}
                          slotProps={{ htmlInput: { 'aria-label': `PIX de ${row.label}`, maxLength: 1024 } }}
                          sx={{ minWidth: 180 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={row.paid ? 'contained' : 'outlined'}
                          color={row.paid ? 'success' : 'inherit'}
                          onClick={() => void toggleFixedPaid(row)}
                        >
                          {row.paid ? 'Pago' : 'Não pago'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{brl.format(fixedTotal / 100)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {fixedPage.pager}
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Typography variant="h6" component="h2" sx={sectionTitleSx}>
              Ressarcimentos
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <TextField
                  select
                  size="small"
                  label="Pessoa"
                  value={reimbPayeeId}
                  onChange={(event) => setReimbPayeeId(event.target.value)}
                  slotProps={{ select: { native: true } }}
                >
                  {sheet.payees.map((payee) => (
                    <option key={payee.id} value={payee.id}>
                      {payee.name}
                    </option>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Valor"
                  value={reimbAmount}
                  onChange={(event) => setReimbAmount(event.target.value)}
                  placeholder="0,00"
                />
                <TextField
                  size="small"
                  label="Observação"
                  value={reimbNote}
                  onChange={(event) => setReimbNote(event.target.value)}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => void addReimbursement()} sx={{ height: 40, alignSelf: 'flex-start' }}>
                  Lançar
                </Button>
              </Stack>

              <Stack spacing={2} sx={{ mt: 3 }}>
                {sheet.payees.map((payee) => {
                  const allRows = reimbursementByPayee.get(payee.id) ?? [];
                  const visible = new Set(reimbPage.slice.map((row) => row.id));
                  const rows = allRows.filter((row) => visible.has(row.id));
                  if (rows.length === 0) return null;
                  const subtotal = allRows.reduce((sum, row) => sum + row.amountCents, 0);
                  return (
                    <Box key={payee.id}>
                      <Typography sx={{ fontWeight: 800 }}>{payee.name}</Typography>
                      {rows.map((row) => (
                        <Stack key={row.id} direction="row" spacing={1} sx={{ alignItems: 'center', py: 0.5 }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {brl.format(row.amountCents / 100)}
                            {row.note ? ` · ${row.note}` : ''}
                          </Typography>
                          <IconButton aria-label="Remover ressarcimento" size="small" onClick={() => void removeReimbursement(row.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Typography variant="body2" color="text.secondary">
                        Total {brl.format(subtotal / 100)}
                      </Typography>
                    </Box>
                  );
                })}
                {reimbPage.pager}
                {sheet.reimbursements.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum ressarcimento neste mês. O total de cada pessoa entra na folha ao lado.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            <Typography variant="h6" component="h2" sx={{ ...sectionTitleSx, mt: 4 }}>
              Limpezas do escritório
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <TextField
                  type="date"
                  size="small"
                  label="Data"
                  value={cleaningDate}
                  onChange={(event) => setCleaningDate(event.target.value)}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: `${competence}-01`, max: `${competence}-31` } }}
                />
                <TextField
                  size="small"
                  label="Valor"
                  value={cleaningAmount}
                  onChange={(event) => setCleaningAmount(event.target.value)}
                  placeholder="0,00"
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => void addCleaning()} sx={{ height: 40, alignSelf: 'flex-start' }}>
                  Lançar
                </Button>
              </Stack>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {cleaningPage.slice.map((row) => (
                  <Stack key={row.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {row.occurredOn.split('-').reverse().join('/')} · {brl.format(row.amountCents / 100)}
                    </Typography>
                    <IconButton aria-label="Remover limpeza" size="small" onClick={() => void removeCleaning(row.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                {cleaningPage.pager}
                <Typography sx={{ fontWeight: 800 }}>Total {brl.format(cleaningTotal / 100)}</Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      <AlertModal isOpen={Boolean(alert)} onClose={() => setAlert('')} message={alert} type="error" />
    </>
  );

  if (embedded) return content;

  return (
    <PageContainer
      maxWidth="xl"
      icon={<AccountBalanceWalletOutlinedIcon />}
      title="Financeiro"
      subtitle="Pagamentos do mês: salários, ressarcimentos e contas fixas."
    >
      {content}
    </PageContainer>
  );
};

export default PaymentsPage;
